import { routeTapes, tapeAllowed } from './tapes';
import { FEED7, RU_FEED } from '../lib/feedContext';
/**
 * Лента: регулятор, строки, свёрнутый хвост и честный ответ на пустоту.
 *
 * Пустых состояний три, и путать их нельзя. Пустая лента при живом соединении
 * означает разное: спокойный рынок, выключенный движок или молчащие площадки.
 * Одно слово «пусто» на все три случая — это способ дать человеку сидеть перед
 * сломанным сканером, не зная об этом.
 */

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useSession } from '../access/session';
import { placeMark } from '../lib/align';
import { tempoOf, useSettings } from '../store/settings';
import { useRing } from './alarm';
import { excludeSaid, useAccounts } from './accounts';
import { densityHonest, emptyView } from './empty';
import { gateText, okGate } from './gate';
import { Calc, useHeldCalcRow } from './Calc';
import { Detail, popOut } from './Detail';
import { saveLayout, useDeskLayout } from './deskLayout';
import { putClip, shelf, toggleClip, useClips } from './clip';
import { keepExtAwake } from './act';
import { BLANK_CALC_ID } from '../lib/row';
import { matchTitle } from './matchTitle';
import { popCalc } from './pop';
import { type Sifted, anyFilter, lineOf, useFilters, useSifted } from './filters';
import { secs } from './format';
import { profitText, useShow } from './show';
import { Honest } from './Honest';
import { Hunt } from './Hunt';
import { Loading } from './Loading';
import { useKeys } from './keys';
import { useProfiles, useTell } from './tell';
import { useEntry } from './entry';
import { type Meta, venueName } from './meta';
import { Row } from './Row';
import { parked } from './parked';
import { rememberOpened } from './opened';
import { dropProfile, useProfile } from './profile';
import { distrust, type FeedRow, useHiddenRows } from './rows';
import { isDry, liveHref, pageLine, prematchHref, russkieHref } from './route';
import { Shift } from './Shift';
import { Sieve } from './Sieve';
import { useScreen } from './store';
import { ShutMark, TrashMark, TrayWhy } from './TrayMarks';
import { keepWork, useHeldWork } from './workRow';
import { PhoneCalc } from './phone/PhoneCalc';
import { PhoneRow } from './phone/PhoneRow';
import { PhoneSieve } from './phone/PhoneSieve';
import { usePhone } from './phone/usePhone';

/** Плотность по часам: столбики за сутки, текущий час выделен. */
function Hours({ byHour, tz }: { byHour: number[]; tz: string }): React.ReactElement | null {
  if (byHour.length !== 24) return null;
  const peak = Math.max(...byHour, 1);
  const now = tz === 'UTC' ? new Date().getUTCHours() : new Date().getHours();
  return (
    <div className="nx-empty__hours" aria-hidden="true">
      {byHour.map((count, hour) => (
        <i
          key={hour}
          className={hour === now ? 'now' : ''}
          style={{ ['--h' as string]: Math.max(3, Math.round((count / peak) * 100)) }}
        />
      ))}
    </div>
  );
}

function Empty({
  hidden,
  byProfile,
  byExclude,
  byFresh = 0,
}: {
  hidden: number;
  byProfile: number;
  byExclude: number;
  byFresh?: number;
}): React.ReactElement {
  const status = useScreen((s) => s.status);
  const framed = useScreen((s) => s.framed);
  const meta = useScreen((s) => s.meta);
  const gate = useSettings((s) => s.gate);
  const gateBank = useSettings((s) => s.bank);
  const gateFx = useSettings((s) => s.fx);
  const gateUnit = useShow((s) => s.unit);
  const clear = useFilters((s) => s.clear);
  const filtering = useFilters(anyFilter);
  const entryOnly = useFilters((s) => s.entryOnly);
  const setEntryOnly = useFilters((s) => s.setEntryOnly);
  const mainVenues = useFilters((s) => s.mainVenues);
  const profile = useProfile((s) => s.profile);
  const accounts = useAccounts((s) => s.accounts);

  const hour = meta.density.tz === 'UTC' ? new Date().getUTCHours() : new Date().getHours();
  const forks = useScreen((s) => s.forks);
  const locked = pageLine(location.pathname);
  let here = 0;
  let away = 0;
  if (locked !== null) {
    const scope = routeTapes(location.pathname, location.search);
    for (const fork of forks.values()) {
      if (!tapeAllowed(fork, scope)) continue;
      const line = lineOf(fork);
      if (line === locked) here += 1;
      else if (line === 'live' || line === 'prematch') away += 1;
    }
  }
  const view = emptyView({
    status,
    framed,
    proto: meta.proto,
    sources: meta.sources,
    tickBroken: meta.tickBroken,
    tickFailures: meta.tickFailures,
    lastFailureAgeSeconds: meta.lastFailureAgeSeconds,
    hidden,
    filtering,
    byProfile,
    byExclude,
    byFresh,
    entryOnly,
    excludeSaid: excludeSaid(accounts, (code) => venueName(meta, code)),
    byHour: meta.density.byHour,
    hour,
    mine: profile?.mine ?? false,
    mainVenues,
    linkNeed: profile?.link,
    prec: profile?.prec,
    pageLine: locked,
    here,
    away,
  });

  if (view.kind === 'revoked') {
    return (
      <div className="nx-empty">
        лента на другом экране. этот больше не получает цены. закройте лишнюю вкладку
        или подтвердите вход в Telegram.
      </div>
    );
  }
  if (view.kind === 'paused') {
    return (
      <div className="nx-empty">
        обновляемся. лента вернётся сама через несколько секунд, перезагружать вкладку не нужно.
        цены на экране сейчас старые, ставить по ним нельзя.
      </div>
    );
  }
  if (view.kind === 'reconnect') {
    return (
      <div className="nx-empty">
        связи с движком нет, переподключаемся. рынок тут ни при чём: мы вас сейчас просто не
        видим. по ценам, которые остались на экране, ставить нельзя.
      </div>
    );
  }
  // Загрузка держится до ПЕРВОГО КАДРА, а не до открытия сокета: между ними
  // щель в секунды, и пустая лента в этой щели читалась как «рынок спокоен»
  // (07.08, владелец). «Вилок нет» имеет право сказать только пустой кадр.
  if (view.kind === 'loading') {
    return <Loading />;
  }

  if (FEED7 && forks.size === 0 && meta.sources.length > 0 && meta.sources.every(s => s.stale)) {
    return <div className="nx-empty">лента 7 временно не получает свежие данные. Старые вилки убраны; фильтры сохранены.</div>;
  }

  if (view.kind === 'tick_broken') {
    return (
      <div className="nx-empty">
        такт пересчёта падал, лента сейчас не рынок. ставить по пустому экрану нельзя: это
        поломка сканера, а не отсутствие вилок.
      </div>
    );
  }

  if (view.kind === 'all_silent') {
    return (
      <div className="nx-empty">
        все площадки молчат: новой цены нет дольше их порога. лента пустая поэтому, а не потому,
        что вилок нет.
      </div>
    );
  }
  if (view.kind === 'majority_silent') {
    return (
      <div className="nx-empty">
        молчит {view.silent.length === 1 ? 'источник' : 'источников'}{' '}
        <b>
          {view.silent.length} из {meta.sources.length}
        </b>{' '}
        ({view.silent.join(', ')}). их новой цены у нас нет, поэтому и пусто. рынок тут ни при
        чём.
      </div>
    );
  }

  if (view.kind === 'required_venue') {
    const names = view.silent.map((code) => venueName(meta, code)).join(', ');
    const whom = view.silent.length === 1 ? 'этой конторы' : 'этих контор';
    return (
      <div className="nx-empty">
        нет {names} на ноге: главная режет пары без {whom}. скрыто <b>{hidden}</b>. снимите
        главную, если хотите видеть остальные пары.{' '}
        <button
          type="button"
          className="nx-empty__clear"
          onClick={() => {
            clear();
            if (byProfile > 0) dropProfile();
          }}
        >
          снять фильтры
        </button>
      </div>
    );
  }

  if (view.kind === 'entry_only') {
    return (
      <div className="nx-empty">
        «только вход» спрятал <b>{hidden}</b>{' '}
        {hidden === 1 ? 'строку' : hidden < 5 ? 'строки' : 'строк'}: на экране нет двери
        нужной точности, хотя HOT не пуст. это не спокойный рынок.
        <button
          type="button"
          className="nx-empty__clear"
          onClick={() => {
            // Сброс сита тумблер входа не снимает — здесь пустоту делает он, снимаем явно.
            clear();
            setEntryOnly(false);
            if (byProfile > 0) dropProfile();
          }}
        >
          снять фильтры
        </button>
      </div>
    );
  }

  if (view.kind === 'exclude') {
    return (
      <div className="nx-empty">
        {view.said ?? 'исключён матч у конторы'}. скрыто <b>{hidden}</b>
        {hidden === 1 ? ' строка' : hidden < 5 ? ' строки' : ' строк'}, хотя рынок не пуст.
      </div>
    );
  }

  if (view.kind === 'away') {
    const other = locked === 'live' ? 'прематч' : 'лайв';
    const href = RU_FEED ? russkieHref(locked === 'live' ? 'prematch' : 'live')
      : locked === 'live' ? prematchHref() : liveHref();
    const mine = locked === 'live' ? 'лайв' : 'прематч';
    return (
      <div className="nx-empty">
        на этой странице нет {mine}-вилок. {other} — на{' '}
        <a href={href}>соседней странице</a>.
      </div>
    );
  }

  if (view.kind === 'filter') {
    return (
      <div className="nx-empty">
        {byProfile > 0 && profile !== null ? (
          <>
            профиль «{profile.name}» скрыл <b>{hidden}</b>{' '}
            {hidden === 1 ? 'строку' : hidden < 5 ? 'строки' : 'строк'}, хотя рынок не пуст.{' '}
          </>
        ) : (
          <>
            ваш фильтр скрыл <b>{hidden}</b>{' '}
            {hidden === 1 ? 'строку' : hidden < 5 ? 'строки' : 'строк'}, хотя рынок не пуст.{' '}
          </>
        )}
        <button
          type="button"
          className="nx-empty__clear"
          onClick={() => {
            clear();
            if (byProfile > 0) dropProfile();
          }}
        >
          снять фильтры
        </button>
      </div>
    );
  }

  if (view.kind === 'engine') {
    return (
      <div className="nx-empty">
        в индексе ноль вилок при живых площадках. это не спокойный рынок — сканер сейчас не
        показывает строки. ставить не по чему.
      </div>
    );
  }

  const byHour = meta.density.byHour;
  const typical = byHour[hour];
  const showHours = densityHonest(byHour, hour);
  return (
    <div className="nx-empty">
      выше вашего порога <b>{gateText(gate, gateUnit, gateBank, gateFx)}</b> сейчас ничего нет. для рынка это обычное дело.
      {showHours && typical !== undefined && (
        <>
          {' '}
          в этот час за сутки движок находил <b>{typical}</b> вилок. столбик ниже показывает, как
          они расходятся по суткам.
        </>
      )}
      {showHours && <Hours byHour={byHour} tz={meta.density.tz} />}
    </div>
  );
}

/**
 * Граница мелочи: не прячет строки, а называет порог.
 *
 * Конкурент хвастается «найдено 12 216 788». Мы говорим другое: вот сколько
 * мелочи есть, вот сколько она даст вместе и вот сколько времени на неё уйдёт.
 * Сумма без времени — это приглашение потратить час ради трёхсот рублей.
 *
 * Раньше это была кнопка «развернуть» с потолком в сорок строк. Свёрнутая по
 * умолчанию лента прячет найденное за клик, а потолок прятал остальное совсем:
 * оба решения экономят экран ценой того, ради чего сюда приходят. Сколько строк
 * крупные, задаёт порог, и он рядом — в шапке, под клавишами «+» и «−».
 */
/**
 * Черта доверия: ниже неё лента опущена не за деньги.
 *
 * Без подписи нарочно. Первая попытка (11.08) была строкой с текстом «ниже — N
 * строк, которым мы не верим», и владелец снял её в тот же час: постоянная
 * фраза посреди ленты читается как ещё одна плашка, а плашки здесь уже отучили
 * читать. Вопрос при этом остался — крупная вилка внизу при порядке «по
 * деньгам» выглядит поломкой сортировки.
 *
 * Ответ найден в форме, а не в словах: пунктир отличается от сплошных линий
 * между строками, поэтому он читается как граница, а не как ещё один
 * разделитель, и молчит, пока на него не наведёшь. Слова живут в подсказке —
 * там их читают один раз и по своей воле.
 *
 * Пояснение — наше, а не браузерное. Первая версия держала его в `title`, и
 * владелец 11.08 сказал ровно то, что должно было насторожить меня самого: «при
 * наведении знак вопроса просто стоит». Курсор со знаком вопроса обещал ответ,
 * который системная подсказка отдаёт через секунду ожидания, а иногда не отдаёт
 * вовсе. Обещание без ответа хуже молчания: линия начинала выглядеть мусором.
 * Поэтому подпись живёт в разметке и появляется мгновенно.
 *
 * `separator` с подписью для чтения с экрана: незрячему пунктир не поможет, а
 * причина порядка нужна ему ровно так же.
 */
function Edge(): React.ReactElement {
  return (
    <div
      className="nx-edge"
      role="separator"
      aria-label="ниже строки, где цена залипла или вызывает сомнения: они опущены при любом порядке"
    >
      <em>ниже цена залипла или под сомнением, такие строки опущены при любом порядке</em>
    </div>
  );
}

function Tail({ rows }: { rows: readonly FeedRow[] }): React.ReactElement {
  const gate = useSettings((s) => s.gate);
  const gateBank = useSettings((s) => s.bank);
  const gateFx = useSettings((s) => s.fx);
  const gateUnit = useShow((s) => s.unit);
  const fx = useSettings((s) => s.fx);
  const attempts = useSettings((s) => s.attempts);
  const unit = useShow((s) => s.unit);
  const tempo = tempoOf(attempts);
  const sum = rows.reduce((acc, row) => acc + row.row.profit, 0);
  // Сумма хвоста — тем же режимом, что прибыль строк. Проценты не складываются:
  // в режиме «%» хвост остаётся в долларах, иначе «вместе 40 %» было бы враньём.
  const together = profitText(sum, 0, fx, unit === 'pct' ? 'usd' : unit).replace(/^\+/, '');
  return (
    <div className="nx-tail" role="separator">
      <b>ещё {rows.length}</b> мельче {gateText(okGate(gate), gateUnit, gateBank, gateFx)}. вместе это {together}
      {tempo !== null && ` и ${secs(rows.length * tempo)} работы`}
    </div>
  );
}

/**
 * Потолок процента обязан кричать, а не шептать.
 *
 * Все прочие фильтры убирают то, что человек и так не берёт. Потолок убирает
 * ВЕРХ ленты — и она после этого выглядит не отфильтрованной, а мёртвой:
 * «выгодных вилок нет вообще». Проверено на живом человеке: кнопка «мягкие»
 * стояла рядом со звуком, и первое случайное попадание опустошило верх молча —
 * мелкая серая надпись «скрыто 40» не читается как причина. Кнопку с тех пор
 * убрали, но потолок всё так же приезжает профилем из студии и живёт в
 * хранилище вкладки неделями.
 *
 * Поэтому полоса стоит НАД лентой, в цвете предупреждения, называет число
 * скрытого и снимает потолок одной кнопкой.
 */
function Ceiling({ ceiling, hidden }: { ceiling: number; hidden: number }): React.ReactElement {
  const setRoi = useFilters((s) => s.setRoi);
  const roiMin = useFilters((s) => s.roiMin);
  const profile = useProfile((s) => s.profile);
  return (
    <div className="nx-cap" role="status">
      стоит потолок <b>{ceiling} %</b>
      {profile !== null && profile.maxRoi > 0 && ` из профиля «${profile.name}»`}, выше него скрыто{' '}
      <b>{hidden}</b> {hidden === 1 ? 'строка' : hidden < 5 ? 'строки' : 'строк'}.
      <button
        type="button"
        onClick={() => {
          setRoi(roiMin, null);
          if (profile !== null && profile.maxRoi > 0) dropProfile();
        }}
      >
        снять потолок
      </button>
    </div>
  );
}

/**
 * Лента, замершая на время чтения.
 *
 * Пока карточка раскрыта, человек читает числа и решает, ставить ли. Живая
 * лента в этот момент воюет с ним: строки выше умирают по несколько штук в
 * секунду, всё под ними съезжает вверх, и раскрытая карточка уползает из-под
 * глаз. Владелец про это: «нажимаю на вилку, начинаю читать, и она в моменте
 * пропадает» (08.08) — и это не про исчезновение строки (она держится
 * призраком), а про то, что её физически уносит с места.
 *
 * Заморозка порядка тут не спасала: она держит ПОРЯДОК, но не мешает строкам
 * выше исчезнуть. Поэтому на время чтения замирает весь список — снимок кадра,
 * сделанный в момент раскрытия. Сама раскрытая вилка при этом живая: её числа
 * берутся из свежего кадра, потому что по ним ставят деньги.
 *
 * Закрыл карточку — лента поехала дальше. Ничего не теряется: кадр приходит
 * заново каждые несколько секунд.
 */
function usePaused(live: Sifted, open: string | null): Sifted {
  // Помнится кадр ДО раскрытия, а не в момент раскрытия. Разница видна руками:
  // взятая в работу строка встаёт в ленте первой, и снимок «в момент» сам бы
  // подкинул её наверх ровно под нажатием — тот же прыжок, от которого пауза и
  // сделана. Замирает та лента, которую человек видел, когда нажимал.
  const before = useRef<Sifted>(live);
  const snap = useRef<{ id: string; view: Sifted } | null>(null);
  if (open === null) {
    snap.current = null;
    before.current = live;
  } else if (snap.current === null || snap.current.id !== open) {
    snap.current = { id: open, view: before.current };
  }
  return snap.current === null ? live : snap.current.view;
}

/**
 * Фокус на раскрытой вилке: всё вокруг уходит в расфокус.
 *
 * Пока человек читает вилку, соседние сто строк — шум с такими же цифрами.
 * Размытие снимает выбор «на что смотреть» вообще (08.08, владелец). Держится
 * ровно до первого прокручивания: покрутил колесо — значит, читать закончил и
 * пошёл дальше по ленте, и мешать ему нельзя.
 *
 * Первые полсекунды прокрутка не считается: раскрытие карточки само двигает
 * страницу, и без этой паузы фокус снимался бы собственным движением.
 */
function useFocus(open: string | null): boolean {
  const [on, setOn] = useState(false);
  useEffect(() => {
    if (open === null) {
      setOn(false);
      return;
    }
    setOn(true);
    const since = Date.now();
    const off = (): void => {
      if (Date.now() - since < 500) return;
      setOn(false);
    };
    window.addEventListener('wheel', off, { passive: true });
    window.addEventListener('touchmove', off, { passive: true });
    window.addEventListener('keydown', off);
    return () => {
      window.removeEventListener('wheel', off);
      window.removeEventListener('touchmove', off);
      window.removeEventListener('keydown', off);
    };
  }, [open]);
  return on;
}

/**
 * Клик мимо карточки — закрыть.
 *
 * Крестик и esc остаются, но тянуться к ним рукой дорого (08.08, владелец):
 * карточка занимает пол-экрана, а закрывается из одной его точки. Промах мимо
 * неё — уже вполне однозначное «дочитал».
 *
 * Три исключения, все обязательные. Сама карточка: внутри неё жмут кнопки,
 * копируют суммы и выделяют текст. Строка ленты: у неё есть собственное
 * переключение, и закрытие здесь спорило бы с ним — по своей же строке нажатие
 * закрыло бы карточку, а следующий за ним клик открыл бы её заново. И стол
 * сверху: карточка стоит под ним, в нём набивают кэф и сумму той же вилки, а
 * заголовок стола переключает карточку сам.
 *
 * Слушается `pointerdown`, а не `click`: он приходит раньше, поэтому нажатие по
 * соседней строке успевает закрыть прошлую карточку до того, как строка
 * откроет свою.
 */
function useShutAside(open: string | null): void {
  useEffect(() => {
    if (open === null) return;
    const aside = (e: PointerEvent): void => {
      const at = e.target;
      if (!(at instanceof Element)) return;
      if (
        at.closest('.nx-det') !== null ||
        at.closest('.nx-fk') !== null ||
        at.closest('.nx-fk__clip') !== null ||
        at.closest('.nx-tray') !== null
      )
        return;
      useScreen.getState().setOpen(null);
    };
    document.addEventListener('pointerdown', aside);
    return () => document.removeEventListener('pointerdown', aside);
  }, [open]);
}

function HiddenList({ rows }: { rows: readonly FeedRow[] }): React.ReactElement {
  const open = useScreen((s) => s.open);
  const meta = useScreen((s) => s.meta);
  const sel = useScreen((s) => s.sel);
  const steps = useScreen((s) => s.steps);
  const freeze = useScreen((s) => s.freeze);
  const leave = useScreen((s) => s.leave);
  const onHover = useCallback((id: string) => useScreen.getState().setSel(id), []);
  const onOpen = useCallback((id: string) => {
    const state = useScreen.getState();
    state.setOpen(state.open === id ? null : id);
  }, []);
  const onPop = useCallback((id: string) => popOut(id), []);

  const ids = useMemo(() => rows.map((row) => row.id), [rows]);
  const opened = open === null ? null : (rows.find((row) => row.id === open) ?? null);

  if (rows.length === 0) {
    return (
      <div className="nx-feed">
        <div className="nx-empty">
          крестиком ничего не убирали. сюда попадают вилки, которые сняли с ленты крестиком
          карточки. живые можно вернуть.
        </div>
      </div>
    );
  }

  return (
    <div className="nx-feed" onMouseEnter={() => freeze(ids)} onMouseLeave={leave}>
      {rows.map((row) => (
        <Fragment key={row.id}>
          <div className="nx-fk-wrap">
            <Row
              row={row}
              meta={meta}
              lead={false}
              selected={row.id === sel}
              held={row.id === open || steps[row.id]?.first != null}
              onOpen={onOpen}
              onPop={onPop}
              onHover={onHover}
            />
          </div>
          {row.id === open && <Detail row={opened ?? row} meta={meta} />}
        </Fragment>
      ))}
    </div>
  );
}

function ClipTray({
  id,
  meta,
  open,
  onOpen,
}: {
  id: string;
  meta: Meta;
  open: boolean;
  onOpen: () => void;
}): React.ReactElement | null {
  const { row, gone } = useHeldCalcRow(id);
  // Keep Calc mounted while hidden: local drafts and manual selections survive.
  const { showCalculator } = useDeskLayout();
  if (row === null) return null;
  const dead = gone || row.ghost !== null;
  return (
    <div
      className={`nx-tray${open ? ' held' : ''}${dead ? ' stale' : ''}`}
      hidden={!showCalculator}
      style={showCalculator ? undefined : { display: 'none' }}
      data-id={row.id}
    >
      <div className="nx-tray__bar">
        <span className="nx-tray__kind">калькулятор</span>
        <span className="nx-tray__gap" />
        {row.id !== BLANK_CALC_ID && (
          <button
            type="button"
            className="nx-tray__shut nx-tray__icon"
            title="снять вилку и её замки — останется пустой калькулятор"
            onClick={() => {
              if (useScreen.getState().open === row.id) useScreen.getState().setOpen(null);
              toggleClip(row.id);
            }}
          >
            <TrashMark />
            <span className="nx-tray__say">сбросить</span>
          </button>
        )}
        <button
          type="button"
          className="nx-tray__shut nx-tray__icon"
          title="спрятать панель: вилка и суммы остаются, лента их не затирает"
          onClick={() => saveLayout({ showCalculator: false })}
        >
          <ShutMark />
          <span className="nx-tray__say">скрыть</span>
        </button>
      </div>
      <div className="nx-tray__who">
        {row.id === BLANK_CALC_ID ? (
          <em className="nx-tray__hint">нажми вилку — встанет сюда; или вбей кэфы руками</em>
        ) : (
          <button type="button" className="nx-tray__title" onClick={onOpen}>
            {matchTitle(row)}
          </button>
        )}
        <TrayWhy />
        {row.row.profit < 0 ? (
          <em className="nx-tray__gone">
            вилка в минусе −{Math.abs(row.row.roiPct).toFixed(2).replace('.', ',')} %
          </em>
        ) : null}
      </div>
      <Calc row={row} meta={meta} face="desk" stale={dead} onFloat={() => popCalc(row.id)} />
    </div>
  );
}

export function Feed(): React.ReactElement {
  const phone = usePhone();
  const { showCalculator } = useDeskLayout();
  // Hiding applies to this visit; returning to the feed starts with a ready calculator.
  useEffect(() => { saveLayout({ showCalculator: true }); }, []);
  const dry = isDry(location.pathname);
  const shelfKind = useScreen((s) => s.shelf);
  const routeScope = routeTapes(location.pathname, location.search);
  const hidRows = useHiddenRows().filter(row => tapeAllowed(row.row.fork, routeScope));
  const live = useSifted();
  const open = useScreen((s) => s.open);
  const shown = usePaused(live, open);
  const clipIds = useClips();
  // Открытая / вынесенная / начатая не имеет права размонтироваться: иначе
  // пелена не на чем висеть, а деньги уже могут стоять на ноге. Полка берётся
  // из живого кадра, лента при открытой карточке — из паузы; без вставки
  // работы закреплённая смерть даёт пустой экран на один кадр и дольше.
  const work = useHeldWork(live.workView ?? live, shown);
  const locked = pageLine(typeof location !== 'undefined' ? location.pathname : '');
  const livePlus = keepWork(live, work, locked, routeScope);
  const shownPlus = keepWork(shown, work, locked, routeScope);
  const pinned = useMemo(
    () => shelf([...livePlus.big, ...livePlus.tail], clipIds).pinned,
    [livePlus, clipIds],
  );
  // Мёртвая остаётся только на полке. Живая — и в калькуляторе, и в ленте:
  // вернулась больше — сама встанет наверх по деньгам, карточку не трогаем.
  const big = useMemo(
    () => shownPlus.big.filter((row) => !clipIds.includes(row.id) || row.ghost === null),
    [shownPlus.big, clipIds],
  );
  const tail = useMemo(
    () => shownPlus.tail.filter((row) => !clipIds.includes(row.id) || row.ghost === null),
    [shownPlus.tail, clipIds],
  );
  const { hidden, hiddenByProfile, hiddenByCeiling, hiddenByExclude, hiddenByFresh, ceiling, stale } = shown;
  // Раскрытая вилка считается по свежему кадру, а не по замершему снимку: по её
  // числам ставят деньги, и показать вместо них картинку минутной давности —
  // это ставка по цене, которой уже нет. Нет в кадре — последняя с пеленой.
  const opened = open === null ? null : (work.find((row) => row.id === open) ?? null);
  useEffect(() => {
    if (open === null || locked === null) return;
    const fork = opened?.row.fork;
    if (!fork) return;
    const line = lineOf(fork);
    if (line !== null && line !== locked) useScreen.getState().setOpen(null);
  }, [open, locked, opened]);
  const meta = useScreen((s) => s.meta);
  const sel = useScreen((s) => s.sel);
  const steps = useScreen((s) => s.steps);
  const freeze = useScreen((s) => s.freeze);
  const leave = useScreen((s) => s.leave);
  const gate = useSettings((s) => s.gate);
  const profile = useProfile((s) => s.profile);
  const bank = useSettings((s) => s.bank);
  const fx = useSettings((s) => s.fx);

  // Раскрытая строка и карточка — один объект. Иначе на строке вчерашняя сумма,
  // в карточке — сегодняшний кэф, и человек ставит не ту ногу.
  const seen = (row: FeedRow): FeedRow =>
    row.id === open && opened !== null ? opened : (work.find((one) => one.id === row.id) ?? row);

  // Купон греем под живой отпечаток и под набитую сумму: уехал кэф или
  // второе плечо пересчиталось — вчерашний тикет в форму не идёт.
  const stakeLock = useScreen((s) => s.stakeLock);
  useEffect(() => {
    const warmOne = (row: FeedRow): void => {
      const total = stakeLock?.forkId === row.id && row.row.staked > 0 ? row.row.staked : bank;
      useEntry.getState().warm(row.id, total, fx, placeMark(row.row.fork, total, fx));
    };
    if (opened !== null) warmOne(opened);
    for (const row of pinned) warmOne(seen(row));
  }, [opened, pinned, bank, fx, stakeLock]);

  // Обработчики берутся из стора в момент нажатия, а не подписками: ссылка на
  // них обязана пережить кадр, иначе `memo` на строке ничего не экономит.
  const onHover = useCallback((id: string) => {
    useScreen.getState().setSel(id);
    const settings = useSettings.getState();
    const fork = useScreen.getState().forks.get(id);
    useEntry.getState().warm(
      id,
      settings.bank,
      settings.fx,
      fork === undefined ? '' : placeMark(fork, settings.bank, settings.fx),
    );
  }, []);
  // Клик по строке — вилка в верхний калькулятор, карточка под ним (владелец
  // 27.09). Пелену при карточке на полке не ставим — лента ниже остаётся живой.
  const [cardAt, setCardAt] = useState<'feed' | 'tray'>('tray');
  // `work` — только уже открытые. Свежая строка ленты там ещё не лежит,
  // и поиск по ней молча ничего не запоминал.
  const catalogRef = useRef<FeedRow[]>([]);
  catalogRef.current = [...live.big, ...live.tail, ...shown.big, ...shown.tail, ...big, ...tail, ...pinned, ...work];
  const recall = (id: string): void => {
    const state = useScreen.getState();
    const row = catalogRef.current.find((one) => one.id === id);
    if (row) rememberOpened(row, state.meta, useSettings.getState().fx, useSession.getState().me.id);
  };
  const onOpen = useCallback((id: string) => {
    const state = useScreen.getState();
    const closing = state.open === id;
    if (!closing) {
      putClip(id);
      recall(id);
    }
    setCardAt('tray');
    state.setOpen(closing ? null : id);
    if (!closing) document.querySelector('.nx-clips')?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, []);
  const onOpenTray = useCallback((id: string) => {
    setCardAt('tray');
    const state = useScreen.getState();
    const closing = state.open === id;
    if (!closing) recall(id);
    state.setOpen(closing ? null : id);
  }, []);
  const onPop = useCallback((id: string) => popOut(id), []);
  const focus = useFocus(open);
  useShutAside(open);
  useEffect(() => keepExtAwake(), []);

  const feedHasOpen =
    open !== null && [...big, ...tail].some((row) => row.id === open);
  const detInClips =
    opened !== null &&
    clipIds.includes(opened.id) &&
    (cardAt === 'tray' || !feedHasOpen);

  useEffect(() => {
    if (open === null || detInClips) return;
    document.querySelector('.nx-feed .nx-det')?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  }, [open, detInClips]);
  const veil = focus && !detInClips;

  // Порядок замирает, пока курсор над лентой или строка выделена. Список,
  // переехавший за время движения руки, — это ставка не по той цене.
  const ids = useMemo(() => [...big, ...tail].map((row) => row.id), [big, tail]);
  // Клавиши получают сами строки, а не их номера: вход в вилку с клавиатуры
  // должен знать ноги, суммы и адреса — то есть ровно то, что в строке и лежит.
  // Полка скрепок входит в тот же список: с клавиатуры её нельзя обойти.
  useKeys(
    useMemo(
      () => (shelfKind === 'hidden' ? hidRows : [...pinned, ...big, ...tail].map(seen)),
      [shelfKind, hidRows, pinned, big, tail, open, opened, work],
    ),
  );
  // Расширению — состав верхних строк: по нему его панель отвечает, какие вилки
  // человеку нечем закрыть. Лента при этом не меняется вовсе.
  useTell(useMemo(() => big.map(seen), [big, open, opened, work]));
  // Профили площадок расширению и его отчёты о промахах — на сервер. Сеть
  // расширению не положена, а лента уже открыта и уже с сессией.
  useProfiles();
  // Сигнал берёт УЖЕ отфильтрованные крупные строки: профиль человека — это
  // его фильтры и его порог, и повторять их правила здесь значило бы завести
  // вторую версию, которая однажды разойдётся с первой.
  // Живые, а не замершие: пауза сделана ради чтения, а не ради того, чтобы
  // человек пропустил вилку, пока читает предыдущую.
  //
  // Порог сигнала — свой, если человек назвал его в студии («звать от»). Иначе
  // зовём от порога ленты. Два разных вопроса: какие строки рисовать крупно и
  // ради каких отрывать человека от другого окна. Раньше `alarmRub` профиля не
  // читал никто, и ползунок «звать от» не делал ничего.
  useRing(
    live.big,
    profile !== null && profile.alarmRub > 0 && (profile.sound || profile.notify)
      ? profile.alarmRub
      : gate,
    profile?.quiet === true,
  );

  // Белая заливка на экране ровно одна. Её получает первая строка, которая
  // прошла все проверки сервера и наши потолки: право вести нельзя выдать
  // просто самой прибыльной, иначе оно перестаёт что-либо значить.
  //
  // Залипшая цена права вести лишает (08.08). Серверный `lead` о ней не знает —
  // покой ноги считается у нас, — и без этой проверки самая заметная строка
  // экрана могла оказаться той, чью цену уже никто не переставляет.
  const leader = big.find(
    (row) => row.ghost === null && row.tier === 'take' && row.row.lead && row.still === null,
  );

  // Граница доверия: с какой строки начинается то, что опущено не за деньги.
  //
  // Сортировка идёт внутри слоёв доверия, а не поперёк них (`distrust`), и без
  // черты это выглядит поломкой: вилка на 5 595 ₽ стоит ниже вилки на 272 ₽ при
  // порядке «по деньгам», и человек справедливо спрашивает, почему (11.08,
  // владелец). Метка в самой строке на этот вопрос не отвечает — она объясняет
  // строку, а не порядок.
  //
  // Считается по последней чистой строке, а не по первой сомнительной: строка «в
  // работе» поднимается наверх при любом порядке, и первой сомнительной может
  // оказаться именно она — тогда черта встала бы под шапкой, отделив чистые от
  // чистых.
  //
  // Только в крупной части ленты. В хвосте лежат мелкие деньги, там своя черта
  // была бы второй линией об одном и том же.
  const lastClean = big.reduce((at, row, i) => (distrust(row) === 0 ? i : at), -1);
  const edge = lastClean >= 0 && lastClean + 1 < big.length ? lastClean + 1 : -1;

  if (shelfKind === 'hidden') {
    return (
      <>
        {!dry && (phone ? <PhoneSieve hidden={0} byProfile={0} /> : <Sieve hidden={0} byProfile={0} />)}
        <HiddenList rows={hidRows} />
      </>
    );
  }

  const line = (row: FeedRow): React.ReactElement => {
    const now = seen(row);
    return (
      <Fragment key={row.id}>
        <div className="nx-fk-wrap">
          {phone ? (
            <PhoneRow
              row={now}
              meta={meta}
              held={row.id === open || steps[row.id]?.first != null || clipIds.includes(row.id)}
              onOpen={onOpen}
            />
          ) : (
            <Row
              row={now}
              meta={meta}
              lead={row.id === leader?.id}
              selected={row.id === sel}
              // Вилка «в работе» остаётся кликабельной и призраком: человеку нужно
              // вернуться к её цифрам, а не смотреть на запертую серую строку.
              held={row.id === open || steps[row.id]?.first != null || clipIds.includes(row.id)}
              onOpen={onOpen}
              onPop={onPop}
              onHover={onHover}
              clipped={clipIds.includes(row.id)}
            />
          )}
        </div>
        {row.id === open && !detInClips && (phone
          ? <PhoneCalc row={opened ?? row} meta={meta} />
          : <Detail row={opened ?? row} meta={meta} />)}
      </Fragment>
    );
  };

  return (
    <>
      {/* Панели регуляторов над лентой больше нет: банк, курс и порог переехали
          в сито (08.08, владелец — «это же чистые фильтры»), а измеренный темп
          — туда же подписью. Лента снова начинается с вилок. */}
      {!dry && (phone ? (
        <PhoneSieve hidden={hidden} byProfile={hiddenByProfile} byExclude={hiddenByExclude} />
      ) : (
        <Sieve hidden={hidden} byProfile={hiddenByProfile} byExclude={hiddenByExclude} />
      ))}
      {!dry && ceiling !== null && hiddenByCeiling > 0 && (
        <Ceiling ceiling={ceiling} hidden={hiddenByCeiling} />
      )}
      {!phone && !showCalculator && (
        <button
          type="button"
          className="nx-tray__show"
          onClick={() => saveLayout({ showCalculator: true })}
        >
          показать калькулятор
        </button>
      )}
        <div
          className={`nx-clips${veil ? ' focus' : ''}`}
          style={phone || (!showCalculator && !detInClips) ? { display: 'none' } : undefined}
        >
          {(clipIds.length > 0 ? clipIds : [BLANK_CALC_ID]).map((id) => (
            <ClipTray
              key={id}
              id={id}
              meta={meta}
              open={id === open}
              onOpen={() => onOpenTray(id)}
            />
          ))}
          {detInClips && opened !== null && <Detail row={opened} meta={meta} />}
        </div>
      <div
        className={`nx-feed${stale === true ? ' stale' : ''}${veil ? ' focus' : ''}`}
        aria-busy={stale === true}
        onMouseEnter={() => freeze(ids)}
        onMouseMove={() => freeze(ids)}
        onMouseLeave={leave}
      >
        {/* Расфокус всего, кроме читаемой вилки. Одним слоем поверх страницы, а
            не размытием каждой строки: строк на экране полтора десятка, и
            пятнадцать фильтров браузер считает пятнадцатью слоями. */}
        {veil && <div className="nx-scrim" aria-hidden="true" />}
        {stale === true && (
          <div className="nx-recount" role="status">
            пересчитываем: числа пока по старому банку
          </div>
        )}
        {/* Замершая лента обязана называться замершей. Молчаливая пауза — это
            сканер, который показывает вчерашний рынок и выглядит исправным. */}
        {open !== null && (
          <div className="nx-hold__say" role="status">
            {opened?.ghost != null
              ? 'карточка сохранена'
              : 'лента стоит, пока открыта карточка · сама вилка при этом живая'}
            <button type="button" onClick={() => useScreen.getState().setOpen(null)}>
              закрыть и продолжить
            </button>
            <i>или нажмите мимо карточки</i>
          </div>
        )}
        {big.length === 0 && tail.length === 0 && pinned.length === 0 && (
          <Empty hidden={hidden} byProfile={hiddenByProfile} byExclude={hiddenByExclude} byFresh={hiddenByFresh} />
        )}
        {big.map((row, i) => (
          <Fragment key={row.id}>
            {i === edge && <Edge />}
            {line(row)}
          </Fragment>
        ))}
        {tail.length > 0 && <Tail rows={tail} />}
        {tail.map((row) => line(row))}
      </div>
      {/* Коридоров здесь больше нет: 12.08 они уехали своей страницей (`/mid`,
          вкладка в шапке). Полка под лентой не работала ни в одном из двух
          состояний — свёрнутую пролистывали не открыв, развёрнутая читалась
          продолжением ленты, где прибыль гарантирована. */}
      {/* Счетов здесь больше нет: 12.08 они уехали в студию, на плитку самой
          конторы (карандаш в разделе «конторы»). Полка стояла свёрнутой под
          лентой и открывалась раз в неделю — то есть занимала место у цен,
          которые смотрят каждую секунду, ради справочника; а отмечать порезку
          человек идёт туда же, где отмечает, что счёт вообще есть. */}
      {/* Колофон сразу после цен: одно число, сколько не показали. Карта часов
          и разбор прихода — приложения, их открывают, а не читают между ставками. */}
      <Hunt unpriced={shown.unpriced} />
      {!parked.hours && <Shift />}
      <Honest meta={meta} />
    </>
  );
}
